import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Tooltip,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { apiClient } from '../../api/httpClient';
import Navbar from '../../globals/components/Navbar';
import TablePaginationActions from "../../globals/components/TablePaginationActions";

const LawFirmAccessRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    // Pagination & Sorting state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");

    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const response = await apiClient('/lawfirm/legal-service-access-requests/');
            setRequests(response);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch requests:", err);
            setError("Failed to load access requests. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setDetailDialogOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailDialogOpen(false);
        setSelectedRequest(null);
    };

    const handleAction = async (requestId, action) => {
        try {
            setActionLoading(requestId);
            await apiClient(`/lawfirm/legal-service-access-requests/${requestId}/`, 'POST', {
                action: action
            });
            toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
            fetchRequests();
            handleCloseDetails();
        } catch (err) {
            console.error(`Failed to ${action} request:`, err);
            toast.error(err.error || `Failed to ${action} request`);
        } finally {
            setActionLoading(null);
        }
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'warning';
        }
    };

    const tableColumns = [
        { id: 'paralegal_name', label: 'Paralegal Name' },
        { id: 'paralegal_email', label: 'Email' },
        { id: 'location_name', label: 'Location' },
        { id: 'message', label: 'Message' },
        { id: 'created_at', label: 'Date Requested' },
        { id: 'status', label: 'Status' }
    ];

    // Sorting Logic
    const sortedRequests = [...requests].sort((a, b) => {
        if (orderBy === 'created_at') {
            return order === 'asc'
                ? new Date(a.created_at) - new Date(b.created_at)
                : new Date(b.created_at) - new Date(a.created_at);
        }
        const valA = (a[orderBy] || '').toString().toLowerCase();
        const valB = (b[orderBy] || '').toString().toLowerCase();
        return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });

    const visibleRows = sortedRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
            <Navbar />
            <Box sx={{ padding: "20px" }}>

                {/* Header Section */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "60vw" }}>
                        <Typography variant="h4" fontWeight="bold">
                            Paralegal Access Requests
                        </Typography>
                        <span style={{ fontSize: "14px", color: "#666" }}>
                            Manage incoming requests from paralegals who wish to join your law firm profile.
                        </span>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            onClick={fetchRequests}
                            startIcon={<RefreshIcon />}
                            disabled={loading}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </Button>
                    </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Table Section */}
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                {tableColumns.map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        sortDirection={orderBy === headCell.id ? order : false}
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleSort(headCell.id)}
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : visibleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No pending requests found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleRows.map((req) => (
                                    <TableRow key={req.request_id} hover>
                                        <TableCell>{req.paralegal_name}</TableCell>
                                        <TableCell>{req.paralegal_email}</TableCell>
                                        <TableCell>{req.location_name}</TableCell>
                                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={req.message}>
                                            {req.message || "—"}
                                        </TableCell>
                                        <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={req.status.toUpperCase()}
                                                color={getStatusColor(req.status)}
                                                size="small"
                                                variant="soft"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleViewDetails(req)}
                                                    size="small"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <TablePagination
          ActionsComponent={TablePaginationActions}
                        component="div"
                        count={requests.length}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25]}
                        sx={{ m: 0 }}
                    />
                </Box>
            </Box>

            {/* Request Detail Dialog */}
            <Dialog open={detailDialogOpen} onClose={handleCloseDetails} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 'bold' }}>Request Details</DialogTitle>
                <DialogContent dividers>
                    {selectedRequest && (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Paralegal Name</Typography>
                                <Typography variant="body1">{selectedRequest.paralegal_name}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                                <Typography variant="body1">{selectedRequest.paralegal_email}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Requested Location</Typography>
                                <Typography variant="body1">{selectedRequest.location_name}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Date Requested</Typography>
                                <Typography variant="body1">{new Date(selectedRequest.created_at).toLocaleString()}</Typography>
                            </Box>
                            {selectedRequest.message && (
                                <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Message</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRequest.message}</Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Current Status</Typography>
                                <Chip
                                    label={selectedRequest.status.toUpperCase()}
                                    color={getStatusColor(selectedRequest.status)}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDetails} color="inherit">Close</Button>
                    {selectedRequest?.status === 'pending' && (
                        <>
                            <Button
                                onClick={() => handleAction(selectedRequest.request_id, 'reject')}
                                color="error"
                                variant="outlined"
                                disabled={actionLoading === selectedRequest.request_id}
                                startIcon={<CancelIcon />}
                            >
                                Reject
                            </Button>
                            <Button
                                onClick={() => handleAction(selectedRequest.request_id, 'approve')}
                                color="success"
                                variant="contained"
                                disabled={actionLoading === selectedRequest.request_id}
                                startIcon={actionLoading === selectedRequest.request_id ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                            >
                                {actionLoading === selectedRequest.request_id ? "Processing..." : "Approve"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LawFirmAccessRequests;
